// W17 — Date-Component Frequency Analysis
// =========================================
// Renders 3 standalone tables (Date 1-31, Month 1-12, Year 4-26) based on
// /api/analysis/date-component-frequency. Each table aggregates appearances
// across Ball 1 to Ball 5 only (no Lucky Stars).
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { CircleNotch, Calendar, CalendarBlank, Clock, CaretUp, CaretDown } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTH_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const DIFF_BADGE = (diff) => {
  if (diff == null) return { cls: 'text-slate-400', txt: '0.00%' };
  const v = Number(diff).toFixed(2);
  if (diff > 1) return { cls: 'text-emerald-700 font-bold', txt: `+${v}%` };
  if (diff > 0) return { cls: 'text-emerald-600', txt: `+${v}%` };
  if (diff < -1) return { cls: 'text-rose-700 font-bold', txt: `${v}%` };
  if (diff < 0) return { cls: 'text-rose-600', txt: `${v}%` };
  return { cls: 'text-slate-500', txt: `${v}%` };
};

const HOT_PCT = (pct, baseline = 10) => {
  if (pct == null) return '';
  if (pct >= baseline + 3) return 'bg-emerald-100 text-emerald-800 font-bold';
  if (pct >= baseline) return 'bg-emerald-50 text-emerald-700';
  if (pct < baseline - 3) return 'bg-rose-100 text-rose-700';
  if (pct < baseline) return 'bg-rose-50 text-rose-600';
  return 'text-slate-700';
};

const GAP_BADGE = (cur, avg) => {
  if (!cur || !avg) return 'text-slate-400';
  const r = cur / avg;
  if (r > 2) return 'bg-rose-200 text-rose-900 font-black';
  if (r > 1.5) return 'bg-rose-100 text-rose-800 font-bold';
  if (r > 1) return 'bg-amber-100 text-amber-800';
  return 'text-slate-700';
};

const SectionTable = ({ title, icon, rows, highlightNum, testidPrefix, baselinePct, monthAbbr }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'number', dir: 'asc' });

  const sorted = useMemo(() => {
    const a = [...rows];
    a.sort((x, y) => {
      const k = sortConfig.key;
      const xv = x[k] ?? 0;
      const yv = y[k] ?? 0;
      const r = (typeof xv === 'number' && typeof yv === 'number') ? xv - yv : String(xv).localeCompare(String(yv));
      return sortConfig.dir === 'desc' ? -r : r;
    });
    return a;
  }, [rows, sortConfig]);

  const handleSort = (k) => setSortConfig(p => ({ key: k, dir: p.key === k && p.dir === 'asc' ? 'desc' : 'asc' }));
  const SortIcon = ({ k }) => sortConfig.key !== k ? null : (sortConfig.dir === 'desc' ? <CaretDown size={9} /> : <CaretUp size={9} />);

  const hCls = "px-1.5 py-1.5 text-[10px] uppercase tracking-tight text-gray-700 cursor-pointer hover:bg-gray-200 text-center whitespace-nowrap";
  const cCls = "px-1.5 py-1 text-center border-b border-gray-100 font-mono text-[11px]";

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`${testidPrefix}-card`}>
      <div className="px-4 py-2.5 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-gray-200 flex items-center gap-2">
        {icon}
        <h3 className="font-bold text-sm text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>{title}</h3>
        <span className="text-[10px] text-slate-500 ml-1">— B1..B5 only · sorted by # by default</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full" data-testid={`${testidPrefix}-table`}>
          <thead className="bg-gray-100 sticky top-0">
            <tr>
              <th className={`${hCls} bg-slate-200 w-12 text-left pl-3`} onClick={() => handleSort('number')}># <SortIcon k="number" /></th>
              <th className={`${hCls} bg-blue-50`} onClick={() => handleSort('till_2025_count')} title="Total appearances (B1..B5) up to end of 2025">×'25 <SortIcon k="till_2025_count" /></th>
              <th className={`${hCls} bg-blue-50`} onClick={() => handleSort('till_2025_pct')} title="% of pre-2026 draws containing this number">% <SortIcon k="till_2025_pct" /></th>
              <th className={`${hCls} bg-emerald-50`} onClick={() => handleSort('in_2026_count')} title="Appearances in 2026 (B1..B5)">×26 <SortIcon k="in_2026_count" /></th>
              <th className={`${hCls} bg-emerald-50`} onClick={() => handleSort('in_2026_pct')}>% <SortIcon k="in_2026_pct" /></th>
              <th className={`${hCls} bg-emerald-50`} onClick={() => handleSort('diff_pct')} title="2026 % minus pre-2026 %">Δ% <SortIcon k="diff_pct" /></th>
              <th className={`${hCls} bg-amber-50`} onClick={() => handleSort('target_month_count')} title={`Total ${monthAbbr} appearances (any year)`}>{monthAbbr} <SortIcon k="target_month_count" /></th>
              <th className={`${hCls} bg-amber-50`} onClick={() => handleSort('target_month_pct')}>M% <SortIcon k="target_month_pct" /></th>
              <th className={`${hCls} bg-amber-50`} onClick={() => handleSort('target_month_diff_pct')} title={`${monthAbbr} % minus overall %`}>Δ% <SortIcon k="target_month_diff_pct" /></th>
              <th className={`${hCls} bg-purple-50`} onClick={() => handleSort('current_gap')} title="Draws since last B1..B5 appearance">Cur Gap <SortIcon k="current_gap" /></th>
              <th className={`${hCls} bg-yellow-50`} onClick={() => handleSort('target_month_gap')} title={`${monthAbbr} seasons missed since last ${monthAbbr} appearance`}>{monthAbbr} Gap <SortIcon k="target_month_gap" /></th>
              <th className={`${hCls} bg-yellow-50`} onClick={() => handleSort('max_target_month_gap')} title={`Longest-ever streak of consecutive ${monthAbbr} seasons this number missed`}>Max {monthAbbr} Gap <SortIcon k="max_target_month_gap" /></th>
              <th className={`${hCls} bg-purple-50`} onClick={() => handleSort('avg_gap')} title="Average gap in draws between consecutive B1..B5 appearances">Avg <SortIcon k="avg_gap" /></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => {
              const isHighlight = highlightNum != null && r.number === highlightNum;
              const d = DIFF_BADGE(r.diff_pct);
              const md = DIFF_BADGE(r.target_month_diff_pct);
              return (
                <tr key={r.number} className={`hover:bg-blue-50 transition-colors ${isHighlight ? 'bg-amber-50' : ''}`} data-testid={`${testidPrefix}-row-${r.number}`}>
                  <td className={`${cCls} font-black text-slate-900 bg-slate-50 text-left pl-3 ${isHighlight ? 'ring-2 ring-amber-500 ring-inset' : ''}`}>
                    {r.number}{isHighlight && <span className="ml-1 text-[9px] font-bold text-amber-700">◀ next</span>}
                  </td>
                  <td className={cCls}>{r.till_2025_count}</td>
                  <td className={`${cCls} ${HOT_PCT(r.till_2025_pct, baselinePct)}`}>{r.till_2025_pct.toFixed(2)}%</td>
                  <td className={`${cCls} ${r.in_2026_count >= 5 ? 'font-bold text-emerald-700' : r.in_2026_count === 0 ? 'text-slate-300' : ''}`}>{r.in_2026_count}</td>
                  <td className={`${cCls} ${HOT_PCT(r.in_2026_pct, baselinePct)}`}>{r.in_2026_pct.toFixed(2)}%</td>
                  <td className={`${cCls} ${d.cls}`}>{d.txt}</td>
                  <td className={cCls}>{r.target_month_count}</td>
                  <td className={`${cCls} ${HOT_PCT(r.target_month_pct, baselinePct)}`}>{r.target_month_pct.toFixed(2)}%</td>
                  <td className={`${cCls} ${md.cls}`}>{md.txt}</td>
                  <td className={`${cCls} ${GAP_BADGE(r.current_gap, r.avg_gap)}`}>{r.current_gap}</td>
                  <td className={`${cCls} ${r.target_month_gap >= 5 ? 'bg-rose-100 text-rose-800 font-bold' : r.target_month_gap >= 1 ? 'text-amber-700' : 'text-slate-500'}`} title={r.last_target_month_year ? `Last ${monthAbbr} appearance: ${r.last_target_month_year}` : `Never appeared in ${monthAbbr}`}>{r.target_month_gap}</td>
                  <td className={`${cCls} ${r.max_target_month_gap >= 5 ? 'bg-rose-100 text-rose-800 font-bold' : r.max_target_month_gap >= 1 ? 'text-amber-700' : 'text-slate-500'}`}>{r.max_target_month_gap}</td>
                  <td className={cCls}>{r.avg_gap}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};


export default function W17DateComponentFrequency({ nextDay, nextMonth, nextYearShort }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        // Always tell the backend which month to profile — defaults to the
        // upcoming draw's month so the "affinity" column tracks the real
        // next draw instead of being frozen on a fixed month.
        const params = nextMonth != null ? { target_month: nextMonth } : {};
        const res = await axios.get(`${API}/analysis/date-component-frequency`, { params });
        if (live) setData(res.data);
      } catch (e) { /* silent */ }
      finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, [nextMonth]);

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-8 flex items-center justify-center">
        <CircleNotch size={28} className="animate-spin text-blue-600" />
        <span className="ml-3 text-sm text-gray-500">Computing date-component frequency…</span>
      </div>
    );
  }
  if (!data) return null;

  const monthAbbr = MONTH_ABBR[((data.target_month || 1) - 1) % 12];
  const monthFull = MONTH_FULL[((data.target_month || 1) - 1) % 12];

  return (
    <div className="space-y-5" data-testid="w17-date-component-frequency">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 flex items-start gap-3 text-xs">
        <Calendar size={18} weight="fill" className="text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-blue-900 leading-relaxed">
          <strong className="text-sm">Date / Month / Year — Component Number Frequency Analysis</strong><br/>
          Complete profile of every possible date-component number (date 1-31, month 1-12, year 4-26) based on its appearance as a winning ball in <strong>Ball 1 to Ball 5</strong> only (Lucky Stars excluded). All metrics — historical %, 2026 %, <strong>{monthFull}</strong> affinity, gap analysis — gathered into one place.
          {(nextDay || nextMonth || nextYearShort != null) && (
            <span className="block mt-1.5 font-mono text-[11px] text-blue-700">
              Next-draw context highlights:
              {nextDay != null && <> day=<span className="font-bold">{nextDay}</span></>}
              {nextMonth != null && <> · month=<span className="font-bold">{nextMonth}</span> ({monthAbbr})</>}
              {nextYearShort != null && <> · year=<span className="font-bold">{nextYearShort}</span></>}
            </span>
          )}
          <span className="block mt-1 text-[10px] text-blue-600 italic">The {monthAbbr} affinity column now tracks whichever month is coming up next — it updates automatically as new draws are added.</span>
        </div>
      </div>

      <SectionTable
        title="📅 Date Number Analysis (1 — 31)"
        icon={<Calendar size={16} weight="bold" className="text-blue-700" />}
        rows={data.dates}
        highlightNum={nextDay}
        testidPrefix="w17-date-table"
        baselinePct={10}
        monthAbbr={monthAbbr}
      />
      <SectionTable
        title="🗓 Month Number Analysis (1 — 12)"
        icon={<CalendarBlank size={16} weight="bold" className="text-orange-700" />}
        rows={data.months}
        highlightNum={nextMonth}
        testidPrefix="w17-month-table"
        baselinePct={10}
        monthAbbr={monthAbbr}
      />
      <SectionTable
        title="📆 Year Number Analysis (4 — 26)"
        icon={<Clock size={16} weight="bold" className="text-purple-700" />}
        rows={data.years}
        highlightNum={nextYearShort}
        testidPrefix="w17-year-table"
        baselinePct={10}
        monthAbbr={monthAbbr}
      />
    </div>
  );
}
